import { SUPPLEMENT_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import type { DosageData } from "@qendo/medication";
import type { ScheduleData } from "@qendo/schedule";
import { OptionType } from "@qendo/types";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { DosageField } from "~/components/form/health-profile/DosageField";
import { YesNo } from "~/components/form/radio/YesNo";
import { ScheduleField } from "~/components/form/schedule/ScheduleField";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Only } from "~/components/permission/Only";
import { Space } from "~/constants/sizing";
import type { Supplement } from "~/entities/medical/supplement";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";
import { dosagesMatch, schedulesMatch } from "~/util/qendo/health-profile";

type SupplementFormProps = {
    readonly supplement?: Supplement;
    onRequestClose: () => void;
} & FullPageModalProps;

export const SupplementForm = ({ supplement, onRequestClose, ...modalProps }: SupplementFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(supplement?.option_id ?? null);
    const [current, setCurrent] = useState<boolean | null>(supplement?.current ?? null);
    const [description, setDescription] = useState(supplement?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(supplement?.photograph_ids ?? []);
    const [dosage, setDosage] = useState<DosageData | null>(supplement?.dosage ?? null);
    const [schedule, setSchedule] = useState<ScheduleData | null>(supplement?.schedule ?? null);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const canSave = canUpdateMedical(MedicalType.supplement) && !saving && option !== null;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (supplement?.option_id ?? null) ||
                description !== (supplement?.description ?? "") ||
                current !== (supplement?.current ?? null) ||
                !dosagesMatch(dosage, supplement?.dosage) ||
                !schedulesMatch(schedule, supplement?.schedule) ||
                !elementsMatch(photographs, supplement?.photograph_ids ?? []),
        );
    }, [supplement, option, description, current, photographs, dosage, schedule]);

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.supplement,
            id: supplement?.id,
            option_id: option,
            photograph_ids: photographs,
            current: current ?? false,
            description,
            dosage,
            schedule,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${SUPPLEMENT_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${supplement?.id ? "Edit" : "Add"} supplement`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.supplements}
                    value={option}
                    onItemSelected={setOption}
                    label="Supplement"
                    disabled={is_saving}
                    name="option_id"
                />
                <DosageField value={dosage} onUpdateDosage={setDosage} disabled={is_saving} label="Supplement form" />
            </FieldGroup>
            <FieldGroup>
                <YesNo title="Do you currently take this?" value={current} onValueChange={setCurrent} />
                <Only when={current}>
                    <ScheduleField
                        value={schedule}
                        onUpdateSchedule={setSchedule}
                        disabled={is_saving}
                        dosage={dosage}
                    />
                </Only>
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
